import {
  Anchor,
  Box,
  Button,
  Container,
  Text,
  Title,
} from '@mantine/core';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bolt,
  ChartBar,
  Checklist,
  Code,
  FileText,
  LayoutKanban,
  Rocket,
  Target,
} from 'tabler-icons-react';

import s from './LandingPage.module.scss';

/* ─── Data ────────────────────────────────── */

const metrics = [
  { label: 'Projects managed', value: '10K+' },
  { label: 'Active users', value: '50K+' },
  { label: 'Uptime', value: '99.9%' },
  { label: 'Integrations', value: '500+' },
];

const heroTestimonials = [
  { quote: 'FlowBoard gave us the transparency we needed without the noise.', name: 'Priya Menon', role: 'Head of Delivery, Vecto' },
  { quote: 'Switching cut our planning cycle in half and highlights blockers before standup.', name: 'Jordan Kim', role: 'Product Ops Lead, Veridian' },
  { quote: 'Finally a Jira alternative that delivers — our team adopted it in a week.', name: 'Alex Rivera', role: 'Engineering Manager, ScaleUp' },
];

const highlights = [
  { title: 'Advanced Issue Management', desc: 'Labels, components, versions, epics, and sub-tasks.', icon: Checklist },
  { title: 'Multiple Project Views', desc: 'Kanban, backlog, roadmap, calendar, list, and deps.', icon: LayoutKanban },
  { title: 'Workflow Automation', desc: 'Automate processes, map workflows, custom fields.', icon: Bolt },
  { title: 'Analytics & Reporting', desc: 'Dashboards, metrics, and comprehensive reports.', icon: ChartBar },
];

const useCases = [
  { title: 'Engineering teams', desc: 'Scrum & Kanban, backlogs, and release planning.', icon: Code },
  { title: 'Product & ops', desc: 'Roadmaps, portfolio, resource view, and dependency graphs.', icon: Target },
  { title: 'QA & delivery', desc: 'Track bugs, bulk operations, and saved filters.', icon: Checklist },
  { title: 'Documentation', desc: 'Specs, runbooks, and meeting notes with search.', icon: FileText },
];

/* ─── Component ───────────────────────────── */

function LandingPage() {
  const navigate = useNavigate();

  return (
    <>
      {/* ══════════════ HERO ══════════════ */}
      <section className={s.hero}>
        <div className={`${s.orb} ${s.orb1}`} />
        <div className={`${s.orb} ${s.orb2}`} />

        <Container size="xl" className="tw-pt-8 tw-pb-24 md:tw-pt-12 md:tw-pb-32">
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-12 lg:tw-gap-16 tw-items-center">
            {/* Left – Copy */}
            <div className="tw-space-y-8">
              <div className="tw-space-y-6">
                <span className={s.sectionBadge} style={{ background: 'rgba(103,232,249,0.15)', color: '#67e8f9', border: '1px solid rgba(103,232,249,0.25)' }}>
                  <Rocket size={14} />
                  Project management, reimagined
                </span>

                <h1 className={s.heroHeadline}>
                  Ship faster.
                  <br />
                  Plan smarter.
                </h1>

                <p className={s.heroSubtext}>
                  One platform for issues, docs, and roadmaps. Kanban, list view, project wiki,
                  and automation — without the complexity. Get started in minutes.
                </p>
              </div>

              <div className="tw-flex tw-flex-wrap tw-gap-3">
                <Button
                  size="lg"
                  radius="xl"
                  variant="gradient"
                  gradient={{ from: 'indigo', to: 'violet' }}
                  onClick={() => navigate('/request-access')}
                  rightIcon={<ArrowRight size={18} />}
                  sx={{ paddingLeft: 28, paddingRight: 28, boxShadow: '0 8px 32px rgba(99,102,241,0.35)' }}
                >
                  Get started free
                </Button>
                <Button
                  size="lg"
                  radius="xl"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
                  }}
                >
                  Sign in
                </Button>
              </div>

              <Text size="xs" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                Free tier: up to 5 users, no credit card required
              </Text>
            </div>

            {/* Right – Kanban Preview */}
            <div className={s.heroVisual}>
              <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <Text size="lg" weight={600}>FlowBoard</Text>
                <div className="tw-flex tw-gap-1">
                  <span className={`${s.viewTab} ${s.active}`}>Kanban</span>
                  <span className={s.viewTab}>List</span>
                  <span className={s.viewTab}>Roadmap</span>
                </div>
              </div>
              <div className={s.heroVisualInner}>
                <div className="tw-grid tw-grid-cols-3 tw-gap-3">
                  <div className={s.kanbanCol}>
                    <Text size="xs" weight={700} mb={8} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>To Do</Text>
                    <div className={s.kanbanCard}>
                      <Text size="xs" weight={600} mb={4}>Setup CI pipeline</Text>
                      <span className={s.kanbanLabel} style={{ background: 'rgba(99,102,241,0.3)', color: '#a5b4fc' }}>Backend</span>
                    </div>
                    <div className={s.kanbanCard}>
                      <Text size="xs" weight={600} mb={4}>Design system audit</Text>
                      <span className={s.kanbanLabel} style={{ background: 'rgba(236,72,153,0.3)', color: '#f9a8d4' }}>Design</span>
                    </div>
                  </div>
                  <div className={s.kanbanCol}>
                    <Text size="xs" weight={700} mb={8} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>In Progress</Text>
                    <div className={s.kanbanCard}>
                      <Text size="xs" weight={600} mb={4}>User auth flow</Text>
                      <span className={s.kanbanLabel} style={{ background: 'rgba(34,197,94,0.3)', color: '#86efac' }}>Feature</span>
                    </div>
                  </div>
                  <div className={s.kanbanCol}>
                    <Text size="xs" weight={700} mb={8} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Done</Text>
                    <div className={s.kanbanCard}>
                      <Text size="xs" weight={600} mb={4}>API endpoints</Text>
                      <span className={s.kanbanLabel} style={{ background: 'rgba(251,191,36,0.3)', color: '#fcd34d' }}>v1.2</span>
                    </div>
                    <div className={s.kanbanCard}>
                      <Text size="xs" weight={600}>DB schema migration</Text>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-4 tw-mt-20">
            {metrics.map((m) => (
              <div key={m.label} className={s.metricCard}>
                <div className={s.metricValue}>{m.value}</div>
                <Text size="sm" sx={{ color: 'rgba(255,255,255,0.5)' }} mt={4}>{m.label}</Text>
              </div>
            ))}
          </div>

          {/* Hero Testimonials */}
          <div className="tw-mt-16">
            <Text size="xs" weight={700} sx={{ color: 'rgba(103,232,249,0.8)', textTransform: 'uppercase', letterSpacing: '0.08em' }} mb="sm">
              What teams say
            </Text>
            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-5">
              {heroTestimonials.map((t) => (
                <div key={t.name} className={s.testimonialCard}>
                  <p className={s.testimonialQuote}>
                    &ldquo;
                    {t.quote}
                    &rdquo;
                  </p>
                  <div className="tw-mt-4">
                    <Text size="sm" weight={600}>{t.name}</Text>
                    <Text size="xs" sx={{ color: 'rgba(255,255,255,0.4)' }}>{t.role}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ══════════════ FEATURE HIGHLIGHTS ══════════════ */}
      <Box className={s.sectionAlt} py="6rem">
        <Container size="xl">
          <div className="tw-text-center tw-mb-12">
            <span className={s.sectionBadge}>Features</span>
            <Title
              order={2}
              mt="md"
              className={s.sectionTitle}
              sx={(theme) => ({
                color: theme.colorScheme === 'dark' ? '#fff' : '#1a1b1e',
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              })}
            >
              Everything you need to deliver
            </Title>
            <Text c="dimmed" mt="sm" maw={560} mx="auto">
              Plan, track, and ship — without switching tools.
            </Text>
          </div>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
            {highlights.map((f) => (
              <div key={f.title} className={s.featureCard}>
                <div className={s.featureIcon}>
                  {React.createElement(f.icon, { size: 26 })}
                </div>
                <Title order={5} mt="md" mb="xs">{f.title}</Title>
                <Text size="sm" c="dimmed">{f.desc}</Text>
              </div>
            ))}
          </div>

          <div className="tw-text-center tw-mt-10">
            <Button
              variant="outline"
              color="violet"
              radius="xl"
              onClick={() => navigate('/features')}
              rightIcon={<ArrowRight size={16} />}
            >
              See all features
            </Button>
          </div>
        </Container>
      </Box>

      {/* ══════════════ USE CASES ══════════════ */}
      <Box py="6rem">
        <Container size="xl">
          <div className="tw-text-center tw-mb-12">
            <span className={s.sectionBadge}>Use cases</span>
            <Title
              order={2}
              mt="md"
              className={s.sectionTitle}
              sx={(theme) => ({
                color: theme.colorScheme === 'dark' ? '#fff' : '#1a1b1e',
                fontSize: 'clamp(1.75rem, 3vw, 2.5rem)',
              })}
            >
              Built for how you work
            </Title>
          </div>

          <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6">
            {useCases.map((uc) => (
              <div key={uc.title} className={s.useCaseCard}>
                <div className={s.featureIcon} style={{ marginBottom: 16 }}>
                  {React.createElement(uc.icon, { size: 26 })}
                </div>
                <Title order={5} mb="xs">{uc.title}</Title>
                <Text size="sm" c="dimmed">{uc.desc}</Text>
              </div>
            ))}
          </div>
        </Container>
      </Box>

      {/* ══════════════ CTA ══════════════ */}
      <Box className={s.sectionDark} py="6rem" style={{ position: 'relative' }}>
        <div className={`${s.ctaGlow} ${s.left}`} />
        <div className={`${s.ctaGlow} ${s.right}`} />
        <Container size="md" style={{ position: 'relative', zIndex: 1 }}>
          <div className="tw-text-center">
            <Title
              order={2}
              ta="center"
              style={{ fontWeight: 800, fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}
            >
              Ready to ship faster?
            </Title>
            <Text
              ta="center"
              style={{ opacity: 0.9 }}
              maw={480}
              mx="auto"
              size="lg"
              mt="sm"
            >
              Join teams who use FlowBoard for clear backlogs,
              real-time roadmaps, and less overhead.
            </Text>
            <Text size="sm" ta="center" style={{ opacity: 0.7 }} mt="xs">
              Free tier: up to 5 users, no credit card required.
            </Text>
            <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-4 tw-mt-8">
              <Button
                size="lg"
                radius="xl"
                variant="white"
                color="violet"
                onClick={() => navigate('/request-access')}
                rightIcon={<ArrowRight size={18} />}
                sx={{ boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}
              >
                Get started free
              </Button>
              <Anchor
                size="sm"
                component="span"
                sx={{
                  color: 'white',
                  opacity: 0.9,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  alignSelf: 'center',
                }}
                onClick={() => navigate('/login')}
              >
                Already have an account? Sign in
              </Anchor>
            </div>
          </div>
        </Container>
      </Box>
    </>
  );
}

export default LandingPage;
